import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { VStack, Text, Button, Divider, Center } from '@chakra-ui/react';
import { memo, useContext, useMemo } from 'react';
import { useT } from '@progress-fe/core';
import { v4 as uuidv4 } from 'uuid';
import { JsFormConfigContext } from '../../../contexts';
import { JsFieldInspector } from '../../jsFormBase';
import { SpecItem } from './components/SpecItem/SpecItem';
/**
 * Component renders "/schemas/jsf-column-specs" field of JsonSchema.
 * @param props : field props come from JsonSchema.
 * Value of props.formData is TColumnSpecsFormData.
 */
const ColumnSpecsJsFieldFC = (props) => {
    const { isInspector } = useContext(JsFormConfigContext) ?? {};
    const { t } = useT();
    const fieldConfig = props.formData.fieldConfig;
    const isEmpty = !props.formData.fieldValues.length;
    const { elementsOptions, parametersMap } = useMemo(() => {
        const elements = [];
        const parameters = {};
        fieldConfig.elementsOptions.forEach((element) => {
            elements.push({ label: element.name, value: element.id });
            parameters[element.id] = element.parameters.map((parameter) => ({
                label: parameter.name,
                value: parameter.id
            }));
        });
        return { elementsOptions: elements, parametersMap: parameters };
    }, [fieldConfig]);
    const handleChangeValue = (value) => {
        props.onChange({
            ...props.formData,
            fieldValues: value
        });
    };
    const handleAdd = () => {
        const valueCopy = [...props.formData.fieldValues];
        const items = Array(fieldConfig.specRowsNum).fill({
            element: null,
            parameter: null,
            value: { value: null },
            acceptableError: { value: null }
        });
        valueCopy.push({ id: uuidv4(), order: valueCopy.length + 1, items });
        handleChangeValue(valueCopy);
    };
    const handleDelete = (deleteId) => {
        const deletedorder = props.formData.fieldValues[deleteId].order;
        // all specs that have higher order should be decreased
        const newValue = props.formData.fieldValues
            .filter((_, idx) => {
            return idx !== deleteId;
        })
            .map((item) => {
            if (item.order > deletedorder) {
                --item.order;
            }
            return item;
        });
        handleChangeValue(newValue);
    };
    const handleSpecChanged = (idx, changedSpec) => {
        const valueCopy = [...props.formData.fieldValues];
        const existSpec = valueCopy[idx];
        const currentOrder = existSpec.order;
        // if order has been changed than switch order with another spec
        if (currentOrder !== changedSpec.order) {
            const specToSwitch = valueCopy.find((item) => item.order === changedSpec.order);
            if (specToSwitch) {
                specToSwitch.order = existSpec.order;
            }
        }
        valueCopy[idx] = changedSpec;
        handleChangeValue(valueCopy);
    };
    return (_jsx(JsFieldInspector, { isActive: isInspector, fieldCode: props.name, fieldType: props.uiSchema?.['ui:field']?.toString() ?? '', valueType: ['object'], children: _jsxs(VStack, { "data-testid": "ColumnSpecsJsField-test", "data-name": props.name, pb: "2px", w: "100%", gap: "8px", children: [isEmpty && (_jsx(Center, { pb: "40px", pt: "40px", children: _jsx(Text, { children: t('jsfields.ColumnSpecsJsField.noItems') }) })), props.formData.fieldValues.map((spec, idx) => (_jsx(SpecItem, { spec: spec, isReadonly: !!props.schema.readOnly, maxOrder: props.formData.fieldValues.length, onChange: (updatedSpec) => handleSpecChanged(idx, updatedSpec), onDelete: () => handleDelete(idx), elementsOptions: elementsOptions, parametersMap: parametersMap }, spec.id))), !props.schema.readOnly && (_jsxs(_Fragment, { children: [_jsx(Divider, {}), _jsx(Button, { variant: "ghost", size: "smSquare", w: "100%", onClick: handleAdd, children: t('jsfields.ColumnSpecsJsField.addSpec') })] }))] }) }));
};
export const ColumnSpecsJsField = memo(ColumnSpecsJsFieldFC);
//# sourceMappingURL=ColumnSpecsJsField.js.map